import { useEffect, useMemo, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import EmployeeSalesBarChart from "./EmployeeSalesBarChart";
import EmployeeTable from "./EmployeeTable";
import ItemSoldTable from "./ItemSoldTable";
import Select from 'react-select'
import { formatSalesValue } from "../../utils/formatUtils";
import OutletSearch from "../SelectSearch/OutletSearch";

const EmployeeAnalysis = () => {
    const today = new Date().toLocaleDateString('en-CA')

    const [selectedOutlet, setSelectedOutlet] = useState("");
    const token = localStorage.getItem("token");
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [loading, setLoading] = useState(false);
    const [employeeData, setEmployeeData] = useState([]);
    const [menuData, setMenuData] = useState([]);
    const [searchByItem, setSearchByItem] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemAnalytics, setItemAnalytics] = useState(null);

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const allMenuItems = useMemo(() => {
        return Array.isArray(menuData) ? menuData : [];
    }, [menuData]);

    const searchOptions = useMemo(() => {
        return allMenuItems.map(item => ({
            value: item.Name,
            label: item.Name,
            data: item
        }));
    }, [allMenuItems]);


    const fetchMenu = async () => {
        if (!token || !selectedOutlet) return;

        setLoading(true);
        await axios
            .post(`${baseUrl}/item-lists-from-orders`, {
                token: token,
                outlet: selectedOutlet,
            })
            .then((response) => {
                setMenuData(response.data);
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                console.log(err);
            });
    }

    const fetchItemAnalytics = async () => {
        if (!selectedItem || !selectedOutlet || !token) {
            toast.error("Please select an item");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/item-analytics`, {
                token,
                itemName: selectedItem.value,
                startDate,
                endDate,
                outlet: selectedOutlet,
            });

            setItemAnalytics(response.data);
        } catch (error) {
            toast.error(error?.response?.data?.error || "Failed to fetch item analytics");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployeeDetails = async () => {
        if (!selectedOutlet || !token) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/employee-analytics`, {
                token: token,
                startDate: startDate,
                endDate: endDate,
                outlet: selectedOutlet,
            });

            const data = response.data || [];
            data.sort((a, b) => b.Total_Sales - a.Total_Sales);
            setEmployeeData(data);
        } catch (error) {
            toast.error(error?.response?.data?.error || "Failed to fetch employee data");
            console.error("Error fetching employee data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOutlet && token) {
            fetchEmployeeDetails();
            fetchMenu();
        }
    }, [selectedOutlet, token]);

    const handleFetchData = async () => {
        if (!selectedOutlet) {
            toast.error("Please select an outlet");
            return;
        }
        if (!startDate || !endDate) {
            toast.error("Please select both dates");
            return;
        }

        await fetchEmployeeDetails();
    };

    const handleIndividualDetails = (employee) => {
        navigate('/employeedetails', {
            state: {
                startdate: startDate,
                enddate: endDate,
                employee_name: employee.Employee,
                outlet_name: selectedOutlet
            }
        });
    }

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl p-3 mb-3 mx-auto">
                    <div className="flex justify-between mb-2.5">
                        <div className="flex-1 flex gap-2 items-center">
                            <label htmlFor="startDate" className="text-md font-semibold">Start Date: </label>
                            <input
                                type="date"
                                id="startDate"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{ margin: "0" }}
                            />
                        </div>

                        <div className="flex-1 flex justify-center">
                            <OutletSearch
                                setSelectedOutlet={setSelectedOutlet}
                                selectedOutlet={selectedOutlet}
                                token={token}
                            />
                        </div>

                        <div className="flex-1 flex gap-2 items-center justify-end">
                            <label htmlFor="endDate" className="text-md font-semibold">End Date: </label>
                            <input
                                type="date"
                                id="endDate"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{ margin: "0" }}
                            />
                        </div>
                    </div>

                    {!searchByItem && (
                        <div className="flex items-center mb-4">
                            <div className="flex-1 flex justify-start">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={searchByItem}
                                        onChange={(e) => {
                                            setSearchByItem(e.target.checked);
                                            setItemAnalytics(null);
                                            setSelectedItem(null);
                                        }}
                                    />
                                    <span className="font-medium text-sm">Search by Item</span>
                                </label>
                            </div>

                            <div className="flex-1 flex justify-center">
                                <button
                                    className="px-3 py-1.5 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
                                    onClick={handleFetchData}
                                    disabled={loading}
                                >
                                    {loading ? "Loading..." : "View"}
                                </button>
                            </div>

                            <div className="flex-1 flex justify-end" />
                        </div>
                    )}

                    {searchByItem && (
                        <div className="flex items-center mb-4">
                            <div className="flex-1 flex justify-start">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={searchByItem}
                                        onChange={(e) => {
                                            setSearchByItem(e.target.checked);
                                            setItemAnalytics(null);
                                            setSelectedItem(null);
                                        }}
                                    />
                                    <span className="font-medium text-sm">Search by Item</span>
                                </label>
                            </div>

                            <div className="flex justify-center items-center w-full max-w-sm z-30">
                                <Select
                                    options={searchOptions}
                                    value={selectedItem}
                                    onChange={setSelectedItem}
                                    isClearable
                                    placeholder="Search menu items..."
                                    className="w-full"
                                />
                            </div>

                            <div className="flex-1 flex justify-end" >
                                <button
                                    className="px-3 py-1.5 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
                                    onClick={fetchItemAnalytics}
                                    disabled={loading}
                                >
                                    {loading ? "Loading..." : "Search"}
                                </button>
                            </div>
                        </div>
                    )}

                    {searchByItem ? (
                        searchByItem && (
                            <>
                                {!itemAnalytics ? (
                                    <div className="mt-6 p-4 rounded-lg border bg-yellow-50 text-yellow-700">
                                        No data available
                                    </div>
                                ) : itemAnalytics.message ? (
                                    <div className="mt-6 p-4 rounded-lg border bg-yellow-50 text-yellow-700">
                                        No data available for the selected item
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 gap-10">
                                        {/* LEFT SIDE - ITEM SUMMARY */}
                                        <div className="shrink-0 space-y-4">
                                            <div>
                                                <div className="text-sm text-gray-500 font-semibold uppercase tracking-wider mb-1">Item</div>
                                                <div className="text-lg font-medium text-gray-800 leading-snug">
                                                    {itemAnalytics.ItemName}
                                                </div>
                                                <div className="text-sm text-gray-500 mt-1 font-medium">
                                                    {itemAnalytics.Group} · {itemAnalytics.ItemType}
                                                </div>
                                            </div>

                                            <div className="border-t border-gray-100 pt-4 space-y-5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-gray-500 font-medium">Rate</span>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {formatSalesValue(itemAnalytics.ItemRate)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-gray-500 font-medium">Total Sales</span>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {formatSalesValue(itemAnalytics.TotalSalesAmount)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-gray-500 font-medium">Quantity Sold</span>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {itemAnalytics.TotalQuantitySold}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-span-2">
                                            <div className="text-sm text-gray-600 font-semibold uppercase tracking-wider bg-stone-100 px-3 py-2">
                                                Employee-wise Sales
                                            </div>

                                            <div className="overflow-auto max-h-[550px] tiny-scrollbar w-full">
                                                <table className="w-full text-center text-sm">
                                                    <thead className="sticky top-0 z-10">
                                                        <tr className="border-b border-gray-100 bg-gray-50 ">
                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                Employee
                                                            </th>
                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                Qty Sold
                                                            </th>
                                                            <th className="px-3 py-2.5 text-xs font-medium text-gray-600 uppercase tracking-wider">
                                                                Total Sales
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {itemAnalytics.EmployeeWiseSales
                                                            .slice()
                                                            .sort((a, b) => b.QuantitySold - a.QuantitySold)
                                                            .map((emp, index) => (
                                                                <tr key={index} className="hover:bg-gray-50/60 transition-colors font-medium">
                                                                    <td className="px-3 py-2 text-gray-700">
                                                                        {emp.Employee}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-gray-500">
                                                                        {emp.QuantitySold}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-gray-800">
                                                                        {formatSalesValue(emp.TotalSales)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    ) : (
                        <>
                            <EmployeeTable
                                employeeData={employeeData}
                                loading={loading}
                                handleIndividualDetails={handleIndividualDetails}
                            />

                            <EmployeeSalesBarChart employeeData={employeeData} />

                            <h3 className="text-lg">Items Sold</h3>

                            <ItemSoldTable data={employeeData} />
                        </>
                    )}
                </div>
            </div>

            <Footer />
        </div >
    );
};

export default EmployeeAnalysis;